import { useCallback, useState } from 'react'

export type RaceView = 'grid' | 'list'

/**
 * Grid or list, remembered across visits.
 *
 * Same standing-preference model as the Inventory page (`inventory:view`): the choice between
 * browsing artwork and scanning a table is not something you want to re-make every time the page
 * loads, so it goes to `localStorage` rather than to component state.
 *
 * The default splits on width because the two views are not equally useful at every size. A race
 * card is image-led and ~430px tall, and below `md` the grid is a single column — so sixteen
 * upcoming races is roughly seven thousand pixels of scroll. The same sixteen as rows is about
 * eleven hundred. On a desktop the grid is two-up and the artwork has somewhere to live, so it
 * stays the default there.
 *
 * `md` (767px) rather than the Inventory page's `sm`, because that is where *this* grid gains its
 * second column.
 */
const VIEW_KEY = 'killrace:view'

const initialView = (): RaceView => {
    try {
        const saved = localStorage.getItem(VIEW_KEY)
        if (saved === 'grid' || saved === 'list') return saved
    } catch {
        // Private-mode Safari throws on localStorage; the width default below is fine.
    }
    return window.matchMedia?.('(max-width: 767px)').matches ? 'list' : 'grid'
}

export const useRaceView = () => {
    const [view, setViewState] = useState<RaceView>(initialView)

    const setView = useCallback((next: RaceView) => {
        setViewState(next)
        try {
            localStorage.setItem(VIEW_KEY, next)
        } catch {}
    }, [])

    return { view, setView }
}
